import { Card, CardContent, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

const formatNumber = (num: number): string => {
    return num.toLocaleString();
};

const PerformerRankingChart: React.FC<DashboardProps> = ({ subscriber, data, isLoading }) => {
    const theme = useTheme();

    if (isLoading) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="rectangular" width="100%" height={300} />
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Stack direction="column" spacing={1} mb={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="h6" gutterBottom>
                                Performer Ranking (Last 30 Days)
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        No data available for performer ranking.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Get the last 30 days data
    const recentData = data.slice(-30);

    // Map the subscriber data using moment
    const subscriberData = recentData.map(day => {
        const dayDate = moment(day._id);
        return {
            date: dayDate.format('YYYY-MM-DD'),
            dateFormatted: moment(day._id).format('MMM D, YYYY'),
            performerTop: day.performerTop || 0,
        };
    });

    // Sort the data by date
    subscriberData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

    return (
        <Card elevation={3}>
            <CardContent>
                <Stack direction="column" spacing={1} mb={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h6" gutterBottom>
                            Performer Ranking (Last 30 Days)
                        </Typography>
                        <ExportButtons
                            data={subscriberData}
                            componentType="performerRanking"
                            filename={`${subscriber.username}_Performer_Ranking`}
                        />
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        {moment().subtract(30, 'days').format('MMM D, YYYY')} - {moment().format('MMM D, YYYY')}
                    </Typography>
                </Stack>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={subscriberData} margin={{ left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dateFormatted" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                        <YAxis
                            tick={{
                                fontSize: 11,
                            }}
                        />
                        <Tooltip
                            labelFormatter={label => `Date: ${label}`}
                            formatter={value => [`Top ${formatNumber(value as number)}%`, undefined]}
                        />
                        <Legend verticalAlign="top" align="right" />
                        <Line
                            type="monotone"
                            dataKey="performerTop"
                            name="Ranking %"
                            strokeWidth={2}
                            stroke={theme.palette.primary.main}
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default PerformerRankingChart;
